import { FC } from "react";
import { TextField, InputLabel } from "@mui/material";
import { UseFormSetValue, FieldValues, get } from "react-hook-form";

import { AddIcon, ArrowDown, Trash, UploadCaptureIcon } from "@bms/assets";
import useWorkReport, { Props, ReceivedProps } from "./hook";
import { Styles } from "./styled";
import { ModalConfirm, ModalDelete, ModalUpload } from "@bms/components";

type PhotoSlotProps = {
  label: string;
  fieldName: string;
  photo: any;
  photoKey?: string;
  isView: boolean;
  loadingImage: string | null;
  openModalUpload: string | null;
  openModalDeleteImg: string | null;
  setValue: UseFormSetValue<FieldValues>;
  handleUrl: (value: File | any) => any;
  onOpenModalUpload: (name: string) => void;
  onCloseModalUpload: () => void;
  onOpenModalDeleteUpload: (name: string) => void;
  onCloseModalDeleteUpload: () => void;
  uploadImage: (name: string, file: any) => void;
  deleteImage: (name: string, key: string) => void;
};

const PhotoSlot: FC<PhotoSlotProps> = ({
  label,
  fieldName,
  photo,
  photoKey,
  isView,
  loadingImage,
  openModalUpload,
  openModalDeleteImg,
  setValue,
  handleUrl,
  onOpenModalUpload,
  onCloseModalUpload,
  onOpenModalDeleteUpload,
  onCloseModalDeleteUpload,
  uploadImage,
  deleteImage,
}) => (
  <div className="item">
    <InputLabel>
      {label} <span>(報告書に記載)</span>
    </InputLabel>
    <div>
      {loadingImage === fieldName ? (
        <p>アップロード中...</p>
      ) : (
        <img
          onClick={() => {
            if (isView) return;
            photo
              ? onOpenModalDeleteUpload(fieldName)
              : onOpenModalUpload(fieldName);
          }}
          src={handleUrl(photo)}
          alt=""
          className={`${photo ? "photo" : ""}`}
        />
      )}
      {(openModalUpload === fieldName || openModalDeleteImg === fieldName) && (
        <>
          <ModalUpload
            beforeUpload={(e) => {
              onCloseModalUpload();
              uploadImage(fieldName, e);
            }}
            name={fieldName}
            url={photo}
            isOpen={Boolean(openModalUpload)}
            setValue={setValue}
            onClose={onCloseModalUpload}
          />
          <ModalDelete
            isOpen={Boolean(openModalDeleteImg)}
            isSubmitting={false}
            onDeleteRecord={async () => {
              if (photoKey) {
                deleteImage(fieldName, photoKey);
              }
              onCloseModalDeleteUpload();
            }}
            onClose={onCloseModalDeleteUpload}
          />
        </>
      )}
    </div>
  </div>
);

const WorkReportLayout: FC<Props> = ({
  register,
  setValue,
  watch,
  onOpenModalUpload,
  onCloseModalUpload,
  onOpenModalDeleteUpload,
  onCloseModalDeleteUpload,
  openModalDeleteImg,
  openModalUpload,
  fields,
  errors,
  isView,
  uploadImage,
  deleteImage,
  loadingImage,
  extraPhotoCounts,
  hiddenMainPhotos,
  collapsedWorkItems,
  collapsedPhotoBlocks,
  addPhotoPair,
  addWorkItem,
  toggleWorkItem,
  togglePhotoBlock,
  openModalDeleteWorkItem,
  onOpenModalDeleteWorkItem,
  onCloseModalDeleteWorkItem,
  confirmDeleteWorkItem,
  openModalDeletePhotoBlock,
  onOpenModalDeletePhotoBlock,
  onCloseModalDeletePhotoBlock,
  confirmDeletePhotoBlock,
}) => {
  const handleUrl = (value: File | any) => {
    if (value?.image_path) {
      return value.image_path;
    }

    if (value?.name) {
      return URL.createObjectURL(value);
    }

    return value || UploadCaptureIcon;
  };

  const renderUploadWrapper = (
    beforePhotoName: string,
    afterPhotoName: string,
    photoNo: number,
    workItemIndex: number,
    extraIndex: number | null,
    onDelete?: () => void
  ) => {
    const beforePhoto = watch(beforePhotoName);
    const afterPhoto = watch(afterPhotoName);
    const beforePhotoKey =
      beforePhoto?.before_photo_key || watch(`${beforePhotoName}_key`);
    const afterPhotoKey =
      afterPhoto?.after_photo_key || watch(`${afterPhotoName}_key`);
    const blockKey = `${workItemIndex}:${
      extraIndex === null ? "main" : extraIndex
    }`;
    const isCollapsed = !!collapsedPhotoBlocks[blockKey];

    const slotProps = {
      isView,
      loadingImage,
      openModalUpload,
      openModalDeleteImg,
      setValue,
      handleUrl,
      onOpenModalUpload,
      onCloseModalUpload,
      onOpenModalDeleteUpload,
      onCloseModalDeleteUpload,
      uploadImage,
      deleteImage,
    };

    return (
      <div
        className={`upload-block${isCollapsed ? " is-collapsed" : ""}`}
        key={blockKey}
      >
        <div className="upload-block-header">
          <div className="upload-block-title">
            <span>作業写真（報告書）({photoNo})</span>
          </div>
          <div className="box-item-actions">
            {!isView && onDelete && (
              <button
                type="button"
                className="delete-work-item-btn"
                onClick={(e) => {
                  e.preventDefault();
                  onDelete();
                }}
              >
                <img src={Trash} alt="写真削除" />
              </button>
            )}
            <button
              type="button"
              className={`toggle-work-item-btn${
                isCollapsed ? "" : " is-expanded"
              }`}
              onClick={(e) => {
                e.preventDefault();
                togglePhotoBlock(workItemIndex, extraIndex);
              }}
              aria-expanded={!isCollapsed}
              aria-label={isCollapsed ? "展開" : "閉じる"}
            >
              <ArrowDown stroke="#347bc3" />
            </button>
          </div>
        </div>
        {!isCollapsed && (
          <div className="upload-block-body">
            <div className="upload-wrapper">
              <PhotoSlot
                {...slotProps}
                label="作業前写真"
                fieldName={beforePhotoName}
                photo={beforePhoto}
                photoKey={beforePhotoKey}
              />
              <PhotoSlot
                {...slotProps}
                label="作業後写真"
                fieldName={afterPhotoName}
                photo={afterPhoto}
                photoKey={afterPhotoKey}
              />
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <Styles className="work-report">
      <div className="main">
        <div className="field-wrapper">
          {fields.map((field, index) => {
            const extraPhotos =
              watch(`work_report_attributes.${index}.extra_photos`) || [];
            const extraCount = Math.max(
              extraPhotos.length,
              extraPhotoCounts[index] ?? 0
            );

            const errorTitle = get(
              errors,
              `work_report_attributes.${index}.title.message`,
              null
            );
            const errorComment = get(
              errors,
              `work_report_attributes.${index}.comment.message`,
              null
            );
            const orderNo = index + 1;
            const isCollapsed = !!collapsedWorkItems[index];
            const workTitle =
              watch(`work_report_attributes.${index}.title`) || "";
            const photoBlocks = [
              ...(!hiddenMainPhotos[index]
                ? [{ extraIndex: null as number | null }]
                : []),
              ...Array.from({ length: extraCount }, (_, extraIndex) => ({
                extraIndex: extraIndex as number | null,
              })),
            ];

            return (
              <div
                className={`box-item${isCollapsed ? " is-collapsed" : ""}`}
                key={field.id}
              >
                <div className="box-item-header">
                  <div className="box-item-title">
                    {/*<span className="box-item-index">{orderNo}</span>*/}
                    <span>作業報告 ({orderNo})</span>
                  </div>
                  <div className="box-item-actions">
                    {!isView && (
                      <button
                        type="button"
                        className="delete-work-item-btn"
                        onClick={(e) => {
                          e.preventDefault();
                          onOpenModalDeleteWorkItem(index);
                        }}
                      >
                        <img src={Trash} alt="作業項目削除" />
                      </button>
                    )}
                    <button
                      type="button"
                      className={`toggle-work-item-btn${
                        isCollapsed ? "" : " is-expanded"
                      }`}
                      onClick={(e) => {
                        e.preventDefault();
                        toggleWorkItem(index);
                      }}
                      aria-expanded={!isCollapsed}
                      aria-label={isCollapsed ? "展開" : "閉じる"}
                    >
                      <ArrowDown stroke="#347bc3" />
                    </button>
                  </div>
                </div>

                {isCollapsed && (
                  <div className="box-item-collapsed-preview">
                    <div className="input-field">
                      <InputLabel>
                        作業タイトル <span>(報告書に記載)({orderNo})</span>
                      </InputLabel>
                      <TextField
                        value={workTitle}
                        placeholder="水道のパッキンが割れてました。"
                        disabled
                      />
                    </div>
                  </div>
                )}

                {!isCollapsed && (
                  <div className="box-item-body">
                    <div className="input-field">
                      <InputLabel>
                        作業タイトル <span>(報告書に記載)({orderNo})</span>
                      </InputLabel>
                      <TextField
                        {...register(`work_report_attributes.${index}.title`)}
                        name={`work_report_attributes.${index}.title`}
                        placeholder="水道のパッキンが割れてました。"
                        disabled={isView}
                      />
                      {errorTitle && <p className="error">{errorTitle}</p>}
                    </div>

                    {photoBlocks.map((block, photoIndex) =>
                      renderUploadWrapper(
                        block.extraIndex === null
                          ? `work_report_attributes.${index}.before_photo`
                          : `work_report_attributes.${index}.extra_photos.${block.extraIndex}.before_photo`,
                        block.extraIndex === null
                          ? `work_report_attributes.${index}.after_photo`
                          : `work_report_attributes.${index}.extra_photos.${block.extraIndex}.after_photo`,
                        photoIndex + 1,
                        index,
                        block.extraIndex,
                        () =>
                          onOpenModalDeletePhotoBlock(index, block.extraIndex)
                      )
                    )}

                    {!isView && (
                      <div className="add-photo-wrapper">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            addPhotoPair(index);
                          }}
                        >
                          <img src={AddIcon} alt="" />
                          <p>写真追加</p>
                        </button>
                      </div>
                    )}

                    <div className="input-field comment-field">
                      <InputLabel>
                        作業コメント{" "}
                        <span>(報告書・簡易報告書に記載)({orderNo})</span>
                      </InputLabel>
                      <TextField
                        multiline
                        rows={2}
                        maxRows={10}
                        {...register(`work_report_attributes.${index}.comment`)}
                        name={`work_report_attributes.${index}.comment`}
                        placeholder="水道のパッキンが割れてました。"
                        disabled={isView}
                      />
                      {errorComment && <p className="error">{errorComment}</p>}
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {!isView && (
            <div className="add-work-item-wrapper">
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  addWorkItem();
                }}
              >
                <img src={AddIcon} alt="" />
                <p>作業項目追加</p>
              </button>
            </div>
          )}
        </div>
      </div>

      <ModalConfirm
        isOpen={openModalDeleteWorkItem}
        isSubmitting={false}
        content="作業項目を削除しますか。"
        onOk={confirmDeleteWorkItem}
        onClose={onCloseModalDeleteWorkItem}
      />

      <ModalConfirm
        isOpen={openModalDeletePhotoBlock}
        isSubmitting={false}
        content="写真を削除しますか。"
        onOk={confirmDeletePhotoBlock}
        onClose={onCloseModalDeletePhotoBlock}
      />
    </Styles>
  );
};

const WorkReport: FC<ReceivedProps> = (props) => (
  <WorkReportLayout {...useWorkReport(props)} />
);

export default WorkReport;
